<script lang="ts" setup>
import { AppLink } from '#components'

const props = defineProps({
  slice: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  slices: {
    type: Array,
    required: true,
  },
  context: {
    type: [Object, Array],
    default: () => null,
  },
})

function getElement(to: string | null | undefined) {
  return to ? AppLink : 'div'
}

const ratios = computed(() => {
  const ratio = {}

  ratio['--ratio-default'] = `${props.slice.File.Media?.width || 7} / ${props.slice.File.Media?.height || 8}`
  ratio['--ratio-mobile'] = `${props.slice.File.MediaMobile?.width || props.slice.File.Media?.width || 33} / ${props.slice.File.MediaMobile?.width || props.slice.File.Media?.width || 40}`

  return ratio
})
</script>

<template>
  <StrapiSlice
    class="ct-richtext-with ct-richtext-with--media"
    :class="{ 'ct-richtext-with--mirrored': slice?.RichtextToTheRight }"
    :slice="slice"
    :index="index"
  >
    <div
      class="ct-richtext-with__grid"
      :class="{ 'ct-richtext-with__grid--background': slice?.BackgroundColor }"
      :style="`background-color: ${slice.BackgroundColor || 'transparent'};`"
    >
      <div
        v-if="slice?.Richtext?.length"
        class="ct-richtext-with__richtext"
        :style="`color: ${slice.Color || 'inherit'}`"
        :class="`ct-richtext-with__richtext--${slice.TextAlignmentWithMedia || 'left'}`"
      >
        <StrapiBlocksText
          :nodes="slice.Richtext"
        />

        <AppLink
          v-if="slice?.Button?.Text && slice?.Button?.Link"
          :to="slice?.Button?.Link"
          class="ct-richtext-with__button"
          :class="`ct-richtext-with__button--${slice?.Button?.Type}${slice?.Button?.Small ? '--small' : ''}`"
        >
          <span>{{ slice?.Button?.Text }}</span>
        </AppLink>
      </div>

      <component
        :is="getElement(slice?.File?.Link)"
        v-if="slice?.File?.Media?.url && slice?.File?.Media?.mime?.includes('image')"
        class="ct-richtext-with__image"
        :to="slice?.File?.Link"
        :style="ratios"
      >
        <picture>
          <source
            :srcset="useXcdnImage(slice?.File?.Media?.url, 'f=rs:fit:680')"
            media="(min-width: 1280px)"
          >
          <source
            :srcset="useXcdnImage(slice?.File?.Media?.url, 'f=rs:fit:640')"
            media="(min-width: 1024px)"
          >
          <source
            :srcset="useXcdnImage(slice?.File?.Media?.url, 'f=rs:fit:512')"
            media="(min-width: 768px)"
          >
          <source
            :srcset="useXcdnImage(slice?.File?.MediaMobile?.url || slice?.File?.Media?.url, 'f=rs:fit:384')"
            media="(min-width: 640px)"
          >
          <source
            :srcset="useXcdnImage(slice?.File?.MediaMobile?.url || slice?.File?.Media?.url, 'f=rs:fit:320')"
          >
          <img
            :src="useXcdnImage(slice?.File?.Media?.url, 'f=rs:fit:680')"
            :alt="slice?.File?.Media?.alternativeText || slice?.File?.Name || ''"
            :width="slice?.File?.Media?.width"
            :height="slice?.File?.Media?.height"
            :loading="index ? 'lazy' : 'eager'"
            :fetchpriority="index ? 'low' : 'high'"
            :decoding="index ? 'async' : 'sync'"
          >
        </picture>
      </component>

      <UiVideo
        v-if="slice?.File?.Media?.url && slice?.File?.Media?.mime?.includes('video')"
        class="ct-richtext-with__video"
        :src="slice?.File?.Media?.url"
        :alt="`${slice.File.Media?.alternativeText || slice.File.Media?.caption || ''}`"
        :width="slice.File.Media.width"
        :height="slice.File.Media.height"
        :loading="index ? 'lazy' : 'eager'"
        :controls="false"
        style="

--ratio-mobile: 375 / 464; --ratio-default: 680 / 640;"
        autoplay
        muted
        loop
      />
    </div>
  </StrapiSlice>
</template>

<style>
@import url("~layers/app/components/Slices/RichtextWith.css");
@import url("~layers/app/components/Slices/RichtextWithMedia.css");
</style>
